<template>
  <div class="workstation">
    <div
      v-if="warehouse === null || warehouse === undefined"
      class="m-5 p-5 text-center bold-40"
    >
      {{ $t("manufacturingCollectList.noWarehouseSelected2") }}
    </div>
    <div
      v-else-if="selectedWarehouse?.type !== 2"
      class="m-5 p-5 text-center bold-40"
    >
      {{ $t("manufacturingCollectList.thisWarehouseNotAProductionType") }}
    </div>
    <div v-else-if="loading" class="col-12">
      <div class="nature-white-box no-min-height mb-3">
        <h2><Skeletor width="30%" /></h2>
        <Skeletor class="p-3 my-1" width="80%" />
        <Skeletor class="p-3 my-1" width="65%" />
      </div>
    </div>
    <div v-else-if="products.length === 0" class="col-12">
      <div class="nature-white-box no-min-height">
        <h2>{{ $t("manufacturingWorkstation.title") }}</h2>
        <div class="m-5 p-5 text-center bold-40">
          {{ $t("manufacturingDesigner.thereIsNoProductOnProduction") }}
        </div>
      </div>
    </div>

    <template v-else>
      <!-- ── FEJLÉC ── -->
      <div class="ws-header nature-white-box no-min-height">
        <div class="ws-header-date">
          <span class="ws-date-full">{{ headerDate }}</span>
          <span class="ws-date-sep">|</span>
          <span class="ws-date-day">{{ headerDay }}</span>
          <span class="ws-date-sep">|</span>
          <span class="ws-date-day">{{ headerClock }}</span>
        </div>
        <div class="ws-header-worker" v-on:click="showWorkerPicker">
          <div class="text-end">
            <div class="ws-worker-name">
              {{ currentWorker?.full_name ?? "–" }}
            </div>
            <div class="ws-worker-role">
              {{ $t("manufacturingWorkstation.worker") }}
            </div>
          </div>
          <div
            class="ws-worker-av"
            :style="{ background: workerColor(currentWorker) }"
          >
            <img
              v-if="currentWorker?.avatar_url"
              :src="currentWorker.avatar_url"
              alt=""
            />
            <span v-else>{{ initials(currentWorker) }}</span>
          </div>
        </div>
      </div>

      <!-- ── TARTALOM ── -->
      <div class="ws-content">
        <div class="ws-center">
          <!-- 1. oszlop: termék -->
          <div class="ws-card">
            <div class="ws-card-lbl">
              {{ $t("manufacturingWorkstation.product") }}
            </div>
            <div class="ws-product-img">
              <img
                v-if="productImage"
                :src="productImage"
                :alt="selectedProduct?.serial"
              />
              <BaseIcon v-else name="box" size="32" />
            </div>
            <div class="ws-product-serial">
              <select
                v-if="products.length > 1"
                class="ws-product-select"
                v-model="selectedSerial"
              >
                <option v-for="p in products" :key="p.serial" :value="p.serial">
                  {{ p.serial }}
                </option>
              </select>
              <span v-else>{{ selectedProduct?.serial }}</span>
            </div>

            <div class="ws-color-label">
              {{ $t("manufacturingWorkstation.colorProduction") }}
            </div>
            <div class="ws-color-btns">
              <button
                v-for="c in colorGroups"
                :key="c.key"
                class="ws-color-btn"
                :class="{ active: c.key === selectedColorKey }"
                v-on:click="selectColor(c.key)"
              >
                <span
                  class="ws-color-dot"
                  :style="{ background: c.hex ? '#' + c.hex : 'transparent' }"
                ></span>
                <span class="text-truncate">{{ c.label }}</span>
              </button>
            </div>

            <div class="ws-size-badges">
              <span
                v-for="row in sizeRows"
                :key="row.id"
                class="ws-size-badge highlight"
              >
                {{ row.size }}
              </span>
            </div>
          </div>

          <!-- 2. oszlop: műveleti adatok -->
          <div class="ws-card">
            <div class="ws-card-lbl">
              {{ $t("manufacturingWorkstation.operationData") }}
            </div>
            <div class="ws-op-header-box">
              <div class="ws-op-num-badge">
                <span class="n">{{
                  currentOp ? currentOpIndex + 1 : "–"
                }}</span>
                <span class="l">Op.</span>
              </div>
              <div class="ws-op-title">
                {{
                  currentOp?.name ?? $t("manufacturingWorkstation.noOperation")
                }}
              </div>
            </div>
            <div class="ws-op-desc-wrap">
              <div v-if="currentOp?.description" class="ws-op-desc-row">
                <div class="ws-op-desc-tag">
                  {{ $t("manufacturingWorkstation.description") }}
                </div>
                <div
                  class="ws-op-desc-text main"
                  v-html="currentOp.description"
                ></div>
              </div>
              <div v-if="currentOp?.extra_info" class="ws-op-desc-row">
                <div class="ws-op-desc-tag">
                  {{ $t("manufacturingWorkstation.extraInfo") }}
                </div>
                <div class="ws-op-desc-text">{{ currentOp.extra_info }}</div>
              </div>
              <div
                v-if="!currentOp?.description && !currentOp?.extra_info"
                class="ws-empty-hint"
              >
                {{ $t("manufacturingWorkstation.noDescription") }}
              </div>

              <div v-if="currentOp?.materials?.length" class="mt-2">
                <div class="ws-op-desc-tag mb-2">
                  {{ $t("manufacturingWorkstation.materials") }}
                </div>
                <div class="ws-materials">
                  <div
                    v-for="(m, i) in currentOp.materials"
                    :key="i"
                    class="ws-material-item"
                  >
                    <img
                      v-if="m.image_url || m.image"
                      class="ws-material-img"
                      :src="mediaUrl(m.image_url ?? m.image)"
                      :alt="m.name"
                    />
                    <div v-else class="ws-material-img ws-material-noimg">
                      <BaseIcon name="box" size="18" />
                    </div>
                    <div class="ws-material-name text-truncate">
                      {{ m.name ?? "–" }}
                    </div>
                    <div v-if="m.amount != null" class="ws-material-right">
                      <div class="ws-material-amt">{{ m.amount }}</div>
                      <div class="ws-material-unit">{{ m.unit ?? "db" }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="currentOp" class="ws-done-row">
              <span class="ws-done-lbl">{{
                $t("manufacturingModal.done")
              }}</span>
              <span class="ws-done-val"
                >{{ doneForCurrentOp }} / {{ totalQty }}</span
              >
            </div>
          </div>

          <!-- 3. oszlop: idők & méretek -->
          <div class="ws-card">
            <div class="ws-card-lbl">
              {{ $t("manufacturingWorkstation.timesAndSizes") }}
            </div>
            <div class="ws-stat-group">
              <div class="ws-info-pill purple">
                <span class="pl">{{
                  $t("manufacturingWorkstation.opTime")
                }}</span>
                <div class="pv-row">
                  <span class="pv">{{ currentOp?.time || "–" }}</span>
                  <span class="pu">{{
                    $t("manufacturingWorkstation.secPerPiece")
                  }}</span>
                </div>
              </div>
              <div class="ws-info-pill yellow text-center">
                <span class="pl">{{
                  $t("manufacturingWorkstation.totalQty")
                }}</span>
                <div class="pv-row justify-content-center">
                  <span class="pv">{{ totalQty || "–" }}</span>
                  <span class="pu">db</span>
                </div>
              </div>
              <div class="ws-info-pill purple text-end">
                <span class="pl">{{
                  $t("manufacturingWorkstation.targetTime")
                }}</span>
                <div class="pv-row justify-content-end">
                  <span class="pv">{{ targetTimeStr }}</span>
                </div>
              </div>
            </div>
            <div class="ws-divider"></div>
            <div class="ws-card-lbl mb-2">
              {{ $t("manufacturingWorkstation.sizeBreakdown") }}
            </div>
            <div class="ws-sizes-grid">
              <div v-for="row in sizeRows" :key="row.id" class="ws-size-item">
                <span class="ws-size-lbl">{{ row.size }}</span>
                <input
                  type="number"
                  class="ws-size-input"
                  min="0"
                  v-model.number="customSizes[row.id]"
                />
              </div>
              <div v-if="sizeRows.length === 0" class="ws-empty-hint">–</div>
            </div>
          </div>

          <!-- 4. oszlop: videó -->
          <div class="ws-card">
            <div class="ws-card-lbl">
              {{ $t("manufacturingWorkstation.videoGuide") }}
            </div>
            <div class="ws-video-wrap">
              <video
                v-if="currentVideo && !currentVideo.remote && videoPlaying"
                :key="currentVideo.id"
                :src="videoSrc(currentVideo)"
                :poster="videoPoster(currentVideo)"
                controls
                autoplay
                muted
                playsinline
                v-on:error="videoError = true"
              ></video>
              <iframe
                v-else-if="currentVideo && currentVideo.remote && videoPlaying"
                :key="'drive-' + currentVideo.id"
                :src="
                  'https://drive.google.com/file/d/' +
                  currentVideo.path +
                  '/preview'
                "
                allow="autoplay"
                allowfullscreen
              ></iframe>
              <div
                v-else-if="currentVideo"
                class="ws-video-play-overlay"
                :style="
                  !currentVideo.remote
                    ? {
                        backgroundImage:
                          'url(' + videoPoster(currentVideo) + ')',
                      }
                    : {}
                "
                v-on:click="videoPlaying = true"
              >
                <div class="ws-video-play-circle">
                  <BaseIcon name="play-circle" size="36" />
                </div>
                <div class="ws-video-play-lbl">
                  {{ $t("manufacturingWorkstation.playVideo") }}
                </div>
              </div>
              <div v-else class="ws-no-video">
                <BaseIcon name="video" size="32" />
                <div class="txt">
                  {{
                    videoError
                      ? $t("manufacturingWorkstation.videoUnavailable")
                      : $t("manufacturingWorkstation.noVideo")
                  }}
                </div>
              </div>
            </div>
            <div v-if="videos.length > 1" class="ws-video-list">
              <button
                v-for="(v, i) in videos"
                :key="v.id"
                class="ws-video-chip"
                :class="{ active: i === currentVideoIndex }"
                v-on:click="selectVideo(i)"
              >
                {{ i + 1 }}
              </button>
            </div>
          </div>
        </div>

        <!-- ── JOBB PANEL ── -->
        <div class="ws-right-panel">
          <div class="ws-action-card">
            <button
              class="ws-start-btn"
              :class="{ running: running }"
              :disabled="!currentOp"
              v-on:click="toggleTimer"
            >
              <BaseIcon :name="running ? 'timer' : 'play-circle'" size="18" />
              <span>{{
                running
                  ? $t("manufacturingWorkstation.pause")
                  : secs > 0
                  ? $t("manufacturingWorkstation.resume")
                  : $t("manufacturingWorkstation.startProduction")
              }}</span>
            </button>
            <div class="ws-timer-box">
              <div class="ws-timer-lbl">
                {{ $t("manufacturingWorkstation.elapsedTime") }}
              </div>
              <div class="ws-timer-val" :class="{ 'timer-running': running }">
                {{ timerStr }}
              </div>
              <div class="ws-timer-target">
                <div class="ws-timer-lbl mb-1">
                  {{ $t("manufacturingWorkstation.targetTime") }}
                </div>
                <div class="ws-timer-target-val">{{ targetTimeStr }}</div>
              </div>
            </div>
            <button
              class="btn btn-primary ws-done-btn"
              :disabled="!currentOp"
              v-on:click="markDone"
            >
              <BaseIcon name="check" size="16" />
              <span>{{ $t("manufacturingWorkstation.done") }}</span>
            </button>
            <div class="ws-spacer"></div>
            <button
              class="ws-problem-btn"
              :disabled="!currentOp"
              v-on:click="reportProblem"
            >
              <BaseIcon name="info-circle" size="16" />
              <span>{{ $t("manufacturingWorkstation.timeProblem") }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- ── LÁBLÉC ── -->
      <div class="ws-footer nature-white-box no-min-height">
        <button
          class="btn btn-outline-primary ws-nav-btn"
          :disabled="currentOpIndex === 0"
          v-on:click="navigate(-1)"
        >
          ← {{ $t("manufacturingWorkstation.previous") }}
        </button>
        <div class="ws-progress-center">
          <div class="ws-progress-dots">
            <div
              v-for="(op, i) in ops"
              :key="op.id"
              class="ws-dot"
              :class="{
                done: i < currentOpIndex,
                active: i === currentOpIndex,
              }"
              :title="op.name"
              v-on:click="goTo(i)"
            ></div>
          </div>
          <div class="ws-op-counter">
            {{
              $t("manufacturingWorkstation.opCounter", {
                current: ops.length ? currentOpIndex + 1 : 0,
                total: ops.length,
              })
            }}
          </div>
        </div>
        <button
          class="btn btn-outline-primary ws-nav-btn"
          :disabled="currentOpIndex >= ops.length - 1"
          v-on:click="navigate(1)"
        >
          {{ $t("manufacturingWorkstation.next") }} →
        </button>
      </div>
    </template>

    <!-- ── DOLGOZÓ VÁLASZTÓ ── -->
    <Modal
      ref="workerModal"
      :title="$t('manufacturingWorkstation.selectYourName')"
    >
      <div v-if="workers.length === 0" class="text-gray">
        {{ $t("manufacturingWorkstation.loading") }}
      </div>
      <div class="ws-wp-list">
        <div
          v-for="w in workers"
          :key="w.id"
          class="ws-wp-item"
          :class="{ active: currentWorker?.id === w.id }"
          v-on:click="selectWorker(w)"
        >
          <div class="ws-wp-av" :style="{ background: workerColor(w) }">
            <img v-if="w.avatar_url" :src="w.avatar_url" alt="" />
            <span v-else>{{ initials(w) }}</span>
          </div>
          <div class="ws-wp-nm">{{ w.full_name }}</div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import http from "@/modules/http";
import tenant from "@/modules/tenant";
import { useStore } from "vuex";
import Modal from "@/components/modals/Modal";
import BaseIcon from "@/components/icons/BaseIcon";

const WORKER_COLORS = [
  "#713AE8",
  "#B460DC",
  "#189a43",
  "#FEC400",
  "#F12B2C",
  "#3751FF",
  "#0EA5E9",
  "#14B8A6",
];

export default {
  name: "ManufacturingWorkstation",
  components: { Modal, BaseIcon },
  data() {
    return {
      store: useStore(),
      loading: true,
      rawItems: [],
      workers: [],
      currentWorker: null,
      selectedSerial: null,
      selectedColorKey: null,
      currentOpIndex: 0,
      customSizes: {},
      videos: [],
      currentVideoIndex: 0,
      videoPlaying: false,
      videoError: false,
      running: false,
      secs: 0,
      timerInterval: null,
      clockInterval: null,
      now: new Date(),
    };
  },
  computed: {
    warehouses() {
      return this.store.state.warehouses;
    },
    warehouse() {
      return this.store.state.topBar.warehouse;
    },
    selectedWarehouse() {
      return this.warehouses.find((e) => e.id === this.warehouse);
    },
    mediaServer() {
      return tenant.getAppMediaUrl();
    },
    videoServer() {
      return tenant.getAppVideoUrl();
    },
    locale() {
      return this.$i18n.locale === "hu"
        ? "hu-HU"
        : this.$i18n.locale === "ro"
        ? "ro-RO"
        : "en-GB";
    },
    headerDate() {
      return this.now.toLocaleDateString(this.locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    headerDay() {
      const d = this.now.toLocaleDateString(this.locale, { weekday: "long" });
      return d.charAt(0).toUpperCase() + d.slice(1);
    },
    headerClock() {
      return this.now.toLocaleTimeString(this.locale, {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    // Gyártás alatt lévő tételek termékenként (serial) csoportosítva
    products() {
      const map = {};
      for (const item of this.rawItems) {
        const key = item.serial ?? item.sku ?? item.id;
        if (!map[key]) {
          map[key] = { serial: key, image: item.image, items: [] };
        }
        map[key].items.push(item);
      }
      return Object.values(map);
    },
    selectedProduct() {
      return (
        this.products.find((p) => p.serial === this.selectedSerial) ??
        this.products[0]
      );
    },
    productImage() {
      const img = this.selectedProduct?.image;
      return img ? this.mediaUrl(img) : null;
    },
    // Terméken belül színenként (szín = egy gyártási sorozat, a méretek a sorai)
    colorGroups() {
      if (!this.selectedProduct) return [];
      const map = {};
      for (const item of this.selectedProduct.items) {
        const hex = item.color_hex ?? null;
        const name = item.caracteristics_1 ?? "";
        const key = (hex ?? "") + "|" + name;
        if (!map[key]) {
          map[key] = {
            key,
            hex,
            label: name || this.$t("manufacturingWorkstation.defaultColor"),
            items: [],
          };
        }
        map[key].items.push(item);
      }
      return Object.values(map);
    },
    selectedColor() {
      return (
        this.colorGroups.find((c) => c.key === this.selectedColorKey) ??
        this.colorGroups[0]
      );
    },
    sizeRows() {
      if (!this.selectedColor) return [];
      return this.selectedColor.items.map((item) => ({
        id: item.id,
        size: item.caracteristics_2 ?? item.sku ?? "–",
        quantity: item.quantity ?? 0,
        item,
      }));
    },
    ops() {
      const first = this.selectedColor?.items?.[0];
      const steps = first?.manufacturing?.steps ?? [];
      return [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    },
    currentOp() {
      return this.ops[this.currentOpIndex] ?? null;
    },
    totalQty() {
      let total = 0;
      for (const row of this.sizeRows) {
        const v = this.customSizes[row.id];
        total += Number.isFinite(v) ? v : row.quantity;
      }
      return total;
    },
    targetTimeStr() {
      const timeSec = this.currentOp?.time ?? 0;
      const targetMin =
        timeSec && this.totalQty
          ? Math.round((timeSec * this.totalQty) / 60)
          : 0;
      if (!targetMin) return "–";
      const h = Math.floor(targetMin / 60);
      const m = targetMin % 60;
      return this.$t("manufacturingWorkstation.hourMinute", { h, m });
    },
    doneForCurrentOp() {
      if (!this.currentOp || !this.selectedColor) return 0;
      let done = 0;
      for (const item of this.selectedColor.items) {
        done += item.manufacturing?.done?.[this.currentOp.id] ?? 0;
      }
      return done;
    },
    timerStr() {
      const h = String(Math.floor(this.secs / 3600)).padStart(2, "0");
      const m = String(Math.floor((this.secs % 3600) / 60)).padStart(2, "0");
      const s = String(this.secs % 60).padStart(2, "0");
      return `${h}:${m}:${s}`;
    },
    currentVideo() {
      return this.videos[this.currentVideoIndex] ?? null;
    },
  },
  watch: {
    warehouses() {
      this.loadData();
    },
    warehouse() {
      this.loadData();
    },
    selectedSerial() {
      this.selectedColorKey = this.colorGroups[0]?.key ?? null;
      this.resetForGroup();
    },
    selectedColorKey() {
      this.resetForGroup();
    },
    currentOp(op) {
      this.loadVideos(op);
    },
  },
  mounted() {
    this.clockInterval = setInterval(() => {
      this.now = new Date();
    }, 10000);
    this.loadWorkers();
    this.loadData();
  },
  beforeUnmount() {
    clearInterval(this.clockInterval);
    clearInterval(this.timerInterval);
  },
  methods: {
    mediaUrl(path) {
      if (!path) return null;
      return path.indexOf("http") === 0
        ? path
        : this.mediaServer + "/" + path.replace(/^\/+/, "");
    },
    videoSrc(video) {
      return this.videoServer + "/" + video.path + "/720p.mp4";
    },
    videoPoster(video) {
      return this.videoServer + "/" + video.path + "/frame1.jpg";
    },
    initials(w) {
      if (!w?.full_name) return "–";
      return w.full_name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p.charAt(0).toUpperCase())
        .join("");
    },
    workerColor(w) {
      if (!w) return "var(--gray-light)";
      const i = this.workers.findIndex((x) => x.id === w.id);
      return WORKER_COLORS[(i < 0 ? 0 : i) % WORKER_COLORS.length];
    },
    loadWorkers() {
      http
        .fetch("/users/")
        .then((data) => {
          this.workers = Array.isArray(data) ? data : data?.data ?? [];
          const savedId = Number(localStorage.getItem("workstation.workerId"));
          const saved = this.workers.find((w) => w.id === savedId);
          if (saved) {
            this.currentWorker = saved;
          } else if (this.store.state.user) {
            this.currentWorker =
              this.workers.find((w) => w.id === this.store.state.user.id) ??
              null;
          }
          if (!this.currentWorker) {
            this.$nextTick(() => this.showWorkerPicker());
          }
        })
        .catch(() => {
          this.workers = [];
        });
    },
    loadData() {
      if (this.selectedWarehouse?.type !== 2) {
        this.loading = false;
        return;
      }
      this.loading = true;
      http
        .fetch("/production/products")
        .then((data) => {
          const production = data?.production ?? [];
          this.rawItems = Array.isArray(production)
            ? production
            : Object.values(production);
          if (
            !this.selectedSerial ||
            !this.products.find((p) => p.serial === this.selectedSerial)
          ) {
            this.selectedSerial = this.products[0]?.serial ?? null;
          }
          if (!this.selectedColorKey) {
            this.selectedColorKey = this.colorGroups[0]?.key ?? null;
          }
          this.resetForGroup();
          this.loading = false;
        })
        .catch(() => {
          this.rawItems = [];
          this.loading = false;
        });
    },
    resetForGroup() {
      this.currentOpIndex = 0;
      const sizes = {};
      for (const row of this.sizeRows) {
        sizes[row.id] = row.quantity;
      }
      this.customSizes = sizes;
      this.resetTimer();
      this.loadVideos(this.currentOp);
    },
    loadVideos(op) {
      this.videos = [];
      this.currentVideoIndex = 0;
      this.videoPlaying = false;
      this.videoError = false;
      if (!op?.id) return;
      http
        .fetch("/files/manufacturing-operation-videos?operation=" + op.id)
        .then((data) => {
          this.videos = Array.isArray(data) ? data : [];
        })
        .catch(() => {
          this.videos = [];
        });
    },
    selectVideo(i) {
      this.currentVideoIndex = i;
      this.videoPlaying = false;
      this.videoError = false;
    },
    selectColor(key) {
      this.selectedColorKey = key;
    },
    showWorkerPicker() {
      this.$refs.workerModal?.showModal();
    },
    selectWorker(w) {
      this.currentWorker = w;
      localStorage.setItem("workstation.workerId", w.id);
      this.$refs.workerModal?.hideModal();
    },
    goTo(i) {
      if (i < 0 || i >= this.ops.length) return;
      this.currentOpIndex = i;
      this.resetTimer();
    },
    navigate(dir) {
      this.goTo(this.currentOpIndex + dir);
    },
    markDone() {
      // A design szerint a "Végeztem" csak lépteti a műveletet; a tényleges
      // készre jelentés a ManufacturingModal-on keresztül történik.
      this.navigate(1);
    },
    reportProblem() {
      this.store.commit("setApiError", {
        message: this.$t("manufacturingWorkstation.timeProblemReported"),
      });
    },
    toggleTimer() {
      this.running = !this.running;
      if (this.running) {
        this.timerInterval = setInterval(() => {
          this.secs++;
        }, 1000);
      } else {
        clearInterval(this.timerInterval);
      }
    },
    resetTimer() {
      clearInterval(this.timerInterval);
      this.running = false;
      this.secs = 0;
    },
  },
};
</script>

<style scoped>
/* A Factory App varroda-tablet designja, a core Prodyflow tokenekre illesztve.
   Lila: --main-grad (#713AE8→#B460DC), státuszok: --green/--yellow/--red,
   szürkék: --gray*, --divider, --bg. Betű: a body Mulish-e. */
.workstation {
  --ws-purple: #713ae8;
  --ws-purple-dark: #5b2bc4;
  --ws-purple-light: #f1ebfd;
  --ws-purple-border: #d9c9fa;
  --ws-yellow-light: #fff7d6;
  --ws-green-dark: #117a34;
  --ws-dark: #252733;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: calc(100vh - 150px);
}

/* ── FEJLÉC ── */
.ws-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px !important;
}
.ws-header-date {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ws-date-full {
  font-size: 18px;
  font-weight: 800;
  color: var(--ws-dark);
}
.ws-date-sep {
  color: var(--gray-light);
}
.ws-date-day {
  font-size: 14px;
  font-weight: 600;
  color: var(--gray);
}
.ws-header-worker {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}
.ws-worker-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--ws-dark);
}
.ws-worker-role {
  font-size: 10px;
  color: var(--gray-light);
}
.ws-worker-av,
.ws-wp-av {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  color: white;
  overflow: hidden;
  flex-shrink: 0;
}
.ws-worker-av img,
.ws-wp-av img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ── TARTALOM ── */
.ws-content {
  flex: 1;
  display: flex;
  gap: 10px;
  min-height: 0;
}
.ws-center {
  flex: 1;
  display: grid;
  grid-template-columns: 150px 1fr 1fr 1fr;
  gap: 10px;
  min-width: 0;
}
.ws-card {
  background: var(--white);
  border-radius: 8px;
  padding: 12px;
  border: 1px solid var(--divider);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  min-width: 0;
}
.ws-card-lbl {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--gray-light);
  margin-bottom: 10px;
  flex-shrink: 0;
}

/* 1. oszlop: termék */
.ws-product-img {
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg);
  border: 2px solid var(--divider);
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-light);
}
.ws-product-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ws-product-serial {
  font-size: 20px;
  font-weight: 800;
  color: var(--ws-purple);
  margin-top: 8px;
  line-height: 1.2;
}
.ws-product-select {
  width: 100%;
  border: 1px solid var(--divider);
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 16px;
  font-weight: 800;
  color: var(--ws-purple);
  background: var(--white);
  font-family: inherit;
}
.ws-color-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: var(--gray-light);
  margin-top: 10px;
  margin-bottom: 6px;
}
.ws-color-btns {
  display: flex;
  flex-direction: column;
  gap: 5px;
  overflow-y: auto;
  min-height: 0;
}
.ws-color-btn {
  padding: 7px 10px;
  border-radius: 8px;
  border: 2px solid var(--divider);
  background: var(--white);
  font-size: 11px;
  font-weight: 700;
  color: var(--ws-dark);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 7px;
  text-align: left;
  font-family: inherit;
  min-width: 0;
}
.ws-color-btn:hover {
  border-color: var(--ws-purple);
  background: var(--ws-purple-light);
}
.ws-color-btn.active {
  border-color: var(--ws-purple);
  background: var(--ws-purple-light);
  color: var(--ws-purple-dark);
}
.ws-color-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--divider);
}
.ws-size-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 8px;
}
.ws-size-badge {
  background: var(--bg);
  border: 1px solid var(--divider);
  border-radius: 5px;
  padding: 3px 7px;
  font-size: 10px;
  font-weight: 700;
  color: var(--gray);
}
.ws-size-badge.highlight {
  background: var(--ws-purple-light);
  border-color: var(--ws-purple-border);
  color: var(--ws-purple-dark);
}

/* 2. oszlop: műveleti adatok */
.ws-op-header-box {
  background: var(--bg);
  border: 1px solid var(--divider);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}
.ws-op-num-badge {
  width: 48px;
  height: 48px;
  background: var(--main-grad);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}
.ws-op-num-badge .n {
  font-size: 20px;
  font-weight: 900;
  line-height: 1;
}
.ws-op-num-badge .l {
  font-size: 8px;
  opacity: 0.7;
}
.ws-op-title {
  flex: 1;
  font-size: 18px;
  font-weight: 800;
  color: var(--ws-dark);
  line-height: 1.2;
  background: var(--ws-purple-light);
  border: 1px solid var(--ws-purple-border);
  border-radius: 8px;
  padding: 8px 10px;
  min-width: 0;
}
.ws-op-desc-wrap {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
}
.ws-op-desc-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.ws-op-desc-tag {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--gray-light);
}
.ws-op-desc-text {
  font-size: 14px;
  color: var(--gray-dark);
  line-height: 1.55;
  font-weight: 500;
}
.ws-op-desc-text.main {
  font-size: 15px;
  color: var(--ws-dark);
  font-weight: 600;
}
.ws-empty-hint {
  font-size: 13px;
  color: var(--gray-light);
  font-style: italic;
}
.ws-materials {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ws-material-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg);
  border: 1px solid var(--divider);
  border-radius: 8px;
  padding: 6px 8px;
}
.ws-material-img {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  background: var(--divider);
}
.ws-material-noimg {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-light);
}
.ws-material-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--ws-dark);
  flex: 1;
  min-width: 0;
}
.ws-material-right {
  margin-left: auto;
  text-align: right;
  flex-shrink: 0;
}
.ws-material-amt {
  font-size: 20px;
  font-weight: 900;
  color: var(--ws-purple);
  line-height: 1;
}
.ws-material-unit {
  font-size: 10px;
  font-weight: 700;
  color: var(--gray-light);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.ws-done-row {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid var(--divider);
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-shrink: 0;
}
.ws-done-lbl {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--gray-light);
}
.ws-done-val {
  font-size: 16px;
  font-weight: 800;
  color: var(--green);
}

/* 3. oszlop: idők & méretek */
.ws-stat-group {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.ws-info-pill {
  flex: 1;
  padding: 8px 10px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  background: var(--bg);
  border: 1px solid var(--divider);
  min-width: 0;
}
.ws-info-pill .pl {
  font-size: 9px;
  font-weight: 700;
  color: var(--gray-light);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.ws-info-pill .pv-row {
  display: flex;
  align-items: baseline;
  gap: 3px;
  margin-top: 2px;
}
.ws-info-pill .pv {
  font-size: 26px;
  font-weight: 900;
  line-height: 1;
}
.ws-info-pill .pu {
  font-size: 10px;
  font-weight: 700;
  opacity: 0.7;
}
.ws-info-pill.purple {
  background: var(--ws-purple-light);
  border-color: var(--ws-purple-border);
}
.ws-info-pill.purple .pv,
.ws-info-pill.purple .pu {
  color: var(--ws-purple);
}
.ws-info-pill.yellow {
  background: var(--ws-yellow-light);
  border-color: #f5dc7a;
}
.ws-info-pill.yellow .pv,
.ws-info-pill.yellow .pu {
  color: #b58a00;
}
.ws-divider {
  height: 1px;
  background: var(--divider);
  margin: 10px 0;
  flex-shrink: 0;
}
.ws-sizes-grid {
  display: flex;
  flex-direction: column;
  gap: 5px;
  overflow-y: auto;
  min-height: 0;
}
.ws-size-item {
  background: var(--ws-purple-light);
  border-radius: 8px;
  padding: 6px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}
.ws-size-lbl {
  font-size: 13px;
  font-weight: 800;
  color: var(--ws-purple-dark);
}
.ws-size-input {
  width: 70px;
  font-size: 18px;
  font-weight: 900;
  color: var(--ws-purple);
  border: none;
  background: transparent;
  text-align: right;
  padding: 0;
  outline: none;
  font-family: inherit;
}
.ws-size-input:focus {
  background: var(--white);
  border-radius: 4px;
  padding: 0 4px;
}

/* 4. oszlop: videó */
.ws-video-wrap {
  background: #0f0a2a;
  border-radius: 8px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  min-height: 120px;
}
.ws-video-wrap video,
.ws-video-wrap iframe {
  width: 100%;
  height: 100%;
  border: 0;
  object-fit: contain;
  background: #000;
}
.ws-video-play-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  height: 100%;
  cursor: pointer;
  background: linear-gradient(135deg, #1e1b4b, #0f0a2a);
  background-size: cover;
  background-position: center;
  transition: filter 0.2s;
}
.ws-video-play-overlay:hover {
  filter: brightness(1.15);
}
.ws-video-play-circle {
  width: 64px;
  height: 64px;
  background: rgba(113, 58, 232, 0.85);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 0 24px rgba(113, 58, 232, 0.6);
}
.ws-video-play-lbl {
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  text-transform: uppercase;
  letter-spacing: 1px;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
}
.ws-no-video {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.35);
}
.ws-no-video .txt {
  font-size: 11px;
}
.ws-video-list {
  display: flex;
  gap: 4px;
  margin-top: 8px;
  flex-shrink: 0;
}
.ws-video-chip {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid var(--divider);
  background: var(--white);
  font-size: 11px;
  font-weight: 700;
  color: var(--gray);
  cursor: pointer;
  font-family: inherit;
}
.ws-video-chip.active {
  border-color: var(--ws-purple);
  background: var(--ws-purple-light);
  color: var(--ws-purple-dark);
}

/* ── JOBB PANEL ── */
.ws-right-panel {
  width: 190px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}
.ws-action-card {
  background: var(--white);
  border-radius: 8px;
  padding: 14px 12px;
  border: 1px solid var(--divider);
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
}
.ws-start-btn {
  width: 100%;
  padding: 16px 10px;
  background: linear-gradient(135deg, var(--green), var(--ws-green-dark));
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: all 0.2s;
  box-shadow: 0 4px 14px rgba(24, 154, 67, 0.25);
  font-family: inherit;
}
.ws-start-btn:hover:not(:disabled) {
  transform: translateY(-2px);
}
.ws-start-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.ws-start-btn.running {
  background: linear-gradient(135deg, var(--yellow), #e0a800);
  color: var(--ws-dark);
  box-shadow: 0 4px 14px rgba(254, 196, 0, 0.3);
}
.ws-timer-box {
  background: var(--bg);
  border: 2px solid var(--divider);
  border-radius: 10px;
  padding: 12px 10px;
  text-align: center;
}
.ws-timer-lbl {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--gray-light);
  margin-bottom: 6px;
}
.ws-timer-val {
  font-size: 20px;
  font-weight: 900;
  color: var(--ws-dark);
  letter-spacing: 2px;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.ws-timer-val.timer-running {
  animation: ws-tick 0.5s ease-in-out infinite alternate;
}
@keyframes ws-tick {
  from {
    color: var(--ws-dark);
  }
  to {
    color: var(--ws-purple);
  }
}
.ws-timer-target {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--divider);
}
.ws-timer-target-val {
  font-size: 22px;
  font-weight: 900;
  color: var(--ws-purple);
  line-height: 1;
}
.ws-spacer {
  flex: 1;
}
.ws-done-btn {
  width: 100%;
  padding: 13px !important;
  border-radius: 10px !important;
  font-size: 14px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.ws-problem-btn {
  width: 100%;
  padding: 13px;
  background: var(--white);
  border: 2px solid var(--red);
  border-radius: 10px;
  color: var(--red);
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
  font-family: inherit;
}
.ws-problem-btn:hover:not(:disabled) {
  background: #fef2f2;
}
.ws-problem-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── LÁBLÉC ── */
.ws-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px !important;
}
.ws-nav-btn {
  padding: 9px 20px !important;
  border-radius: 8px !important;
  font-size: 13px;
  font-weight: 700;
}
.ws-progress-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.ws-progress-dots {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  max-width: 320px;
  justify-content: center;
}
.ws-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--divider);
  transition: all 0.2s;
  flex-shrink: 0;
  cursor: pointer;
}
.ws-dot.done {
  background: var(--green);
}
.ws-dot.active {
  background: var(--ws-purple);
  transform: scale(1.4);
}
.ws-op-counter {
  font-size: 11px;
  color: var(--gray-light);
  font-weight: 600;
}

/* ── DOLGOZÓ VÁLASZTÓ ── */
.ws-wp-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ws-wp-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 2px solid var(--divider);
  cursor: pointer;
  transition: all 0.2s;
}
.ws-wp-item:hover,
.ws-wp-item.active {
  border-color: var(--ws-purple);
  background: var(--ws-purple-light);
}
.ws-wp-av {
  width: 40px;
  height: 40px;
  font-size: 14px;
}
.ws-wp-nm {
  font-size: 14px;
  font-weight: 700;
  color: var(--ws-dark);
}

/* Tablet: a jobb panel a rács alá kerül */
@media (max-width: 1100px) {
  .ws-content {
    flex-direction: column;
  }
  .ws-center {
    grid-template-columns: 130px 1fr 1fr;
  }
  .ws-right-panel {
    width: 100%;
  }
  .ws-action-card {
    flex-direction: row;
    flex-wrap: wrap;
  }
  .ws-action-card > * {
    flex: 1 1 160px;
  }
  .ws-spacer {
    display: none;
  }
}
@media (max-width: 768px) {
  .ws-center {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
